/**
 * Market Watch Filter State Persistence
 * Saves/restores filter selections using OfficeRuntime.storage,
 * decoupled from sheet names (which are now dynamic per run).
 */

import { CUSTOM_PROPERTIES, SHEET_PREFIXES, PRICE_CLUSTER_MODES, MW_FILTER_DEFAULTS } from "./constants";

export interface MarketWatchFilterState {
  asin: string[];
  keyword: string[];
  brand: string[];
  excludedBrands?: string[];
  isExcludeSameBrand?: boolean;
  isExcludeSpreetailAsin?: boolean;
  priceClusterMode?: "global" | "dynamic";
  priceVariancePct?: number;
  varianceGrowthFactor?: number | null;
  isEnabledSeasonality?: boolean;
}

const DEFAULT_STATE: MarketWatchFilterState = {
  asin: [],
  keyword: [],
  brand: [],
  excludedBrands: [],
  isExcludeSameBrand: false,
  isExcludeSpreetailAsin: false,
  priceClusterMode: PRICE_CLUSTER_MODES.GLOBAL,
  priceVariancePct: MW_FILTER_DEFAULTS.PRICE_VARIANCE_PCT,
  varianceGrowthFactor: null,
  isEnabledSeasonality: false,
};

/**
 * Save market watch filter state to OfficeRuntime.storage.
 * @param key - Storage key (typically MARKET_WATCH_SHEET constant)
 * @param state - Filter state to persist
 */
export async function saveMarketWatchFilters(
  key: string,
  state: MarketWatchFilterState
): Promise<void> {
  const value = JSON.stringify(state);
  await OfficeRuntime.storage.setItem(key, value);
}

/**
 * Clear persisted market watch filters from OfficeRuntime.storage.
 * @param key - Storage key (typically MARKET_WATCH_SHEET constant)
 */
export async function clearMarketWatchFilters(key: string): Promise<void> {
  try {
    await OfficeRuntime.storage.removeItem(key);
  } catch (error) {
    console.warn(`Could not clear market watch filters for key "${key}":`, error);
  }
}

/**
 * Load market watch filter state from OfficeRuntime.storage.
 * Returns default empty state when no saved filters exist.
 * Never returns null (storage is always available).
 */
export async function loadMarketWatchFilters(key: string): Promise<MarketWatchFilterState> {
  try {
    const value = await OfficeRuntime.storage.getItem(key);
    if (value) {
      const parsed = JSON.parse(value);
      return {
        asin: parsed.asin || [],
        keyword: parsed.keyword || [],
        brand: parsed.brand || [],
        excludedBrands: parsed.excludedBrands || [],
        isExcludeSameBrand: parsed.isExcludeSameBrand ?? false,
        isExcludeSpreetailAsin: parsed.isExcludeSpreetailAsin ?? false,
        priceClusterMode: parsed.priceClusterMode ?? PRICE_CLUSTER_MODES.GLOBAL,
        priceVariancePct: parsed.priceVariancePct ?? MW_FILTER_DEFAULTS.PRICE_VARIANCE_PCT,
        varianceGrowthFactor: parsed.varianceGrowthFactor ?? null,
        isEnabledSeasonality: parsed.isEnabledSeasonality ?? false,
      };
    }
  } catch (error) {
    console.warn(`Could not load market watch filters for key "${key}":`, error);
  }
  return { ...DEFAULT_STATE };
}

/** Custom property key stored on each MW sheet */
export const MW_FILTERS_PROP_KEY = CUSTOM_PROPERTIES.MW_FILTERS;

/**
 * Tag all visible MW sheets from a run (matching timestamp) with filter state.
 * Call inside an existing Excel.run context after all sheets are created.
 */
export async function tagMWRunSheets(
  context: Excel.RequestContext,
  timestamp: string,
  state: MarketWatchFilterState
): Promise<void> {
  const worksheets = context.workbook.worksheets;
  worksheets.load("items/name");
  await context.sync();

  const filterJSON = JSON.stringify(state);
  for (const sheet of worksheets.items) {
    if (sheet.name.startsWith(SHEET_PREFIXES.MW) && sheet.name.includes(timestamp)) {
      sheet.customProperties.add(MW_FILTERS_PROP_KEY, filterJSON);
    }
  }
  await context.sync();
}

/**
 * Read MW filter state from the active sheet's custom property.
 * Returns null if the active sheet is not an MW sheet or has no saved filters.
 */
export async function loadFiltersFromActiveMWSheet(): Promise<MarketWatchFilterState | null> {
  let result: MarketWatchFilterState | null = null;
  try {
    await Excel.run(async (context) => {
      const activeSheet = context.workbook.worksheets.getActiveWorksheet();
      activeSheet.load("name");
      await context.sync();

      if (!activeSheet.name.startsWith(SHEET_PREFIXES.MW)) return;

      const prop = activeSheet.customProperties.getItemOrNullObject(MW_FILTERS_PROP_KEY);
      prop.load(["isNullObject", "value"]);
      await context.sync();

      if (!prop.isNullObject && prop.value) {
        const parsed = JSON.parse(prop.value as string);
        result = {
          asin: parsed.asin || [],
          keyword: parsed.keyword || [],
          brand: parsed.brand || [],
          excludedBrands: parsed.excludedBrands || [],
          isExcludeSameBrand: parsed.isExcludeSameBrand ?? false,
          isExcludeSpreetailAsin: parsed.isExcludeSpreetailAsin ?? false,
          priceClusterMode: parsed.priceClusterMode ?? PRICE_CLUSTER_MODES.GLOBAL,
          priceVariancePct: parsed.priceVariancePct ?? MW_FILTER_DEFAULTS.PRICE_VARIANCE_PCT,
          varianceGrowthFactor: parsed.varianceGrowthFactor ?? null,
          isEnabledSeasonality: parsed.isEnabledSeasonality ?? false,
        };
      }
    });
  } catch (error) {
    console.warn("Could not load MW filters from active sheet:", error);
  }
  return result;
}
